import { useEffect, useMemo, useRef, useState, Fragment } from "react";
import { Select, DatePicker, Popover, Tooltip, Skeleton, Tag } from "antd";
import { useRecoilState } from "recoil";
import moment from "moment";
import { AgCharts } from "ag-charts-react";
import { AgTableClient } from "app/shared/AgTable";
import PageHeader from "app/shared/PageHeader";
import { getSubdomain } from "app/utils/generic.helper";
import { userInfo } from "app/config/States/users";
import "./HotelEmailFollowUp.css";
import "./ReminderDashboard.css";

function fmt(n) {
  return (n || 0).toLocaleString("en-IN");
}

// Persists filter selections across navigation (e.g. going back to this page
// after drilling into a row) so an accidental back/forward doesn't lose them.
const FILTERS_STORAGE_KEY = "rd-reminder-dashboard-filters";

function loadStoredFilters() {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

const API_GSTIN_DASHBOARD_SUMMARY = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/gstin-transmission/dashboard/summary";
const API_CUSTOMER_DETAILS = "https://stage-apiplatform.finkraftai.com/api/hotelfollowup/customerdetailsnew";
const API_HOTEL_CITY_STATE = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/hotelCityState";
const BREAKDOWN_SEGMENTS = [
  { key: "sentOnTime", color: "#22c55e", label: "Sent on time" },
  { key: "sentLate", color: "#f59e0b", label: "Sent late" },
  { key: "notSent", color: "#ef4444", label: "Not sent" },
  { key: "yetToSend", color: "#94a3b8", label: "Yet to send" },
];

function cleanBreakdownLabel(label, category) {
  if (!label) return "";
  const prefix = category === "hotel" ? /^Reminder To Hotel\s*/i : /^Reminder To Traveller\s*/i;
  return label.replace(prefix, "").trim();
}

// Mapped from the same gstin-transmission/dashboard/summary response's
// clientBreakdown array: { clientId, clientName, totalRecords, total,
// sentOnTime, sentLate, notSent, yetToSend, onTimePercent, received,
// recoveryPercent, hotelStays }. totalRecords is every booking; total is the
// subset actually eligible to send the mail (used for on-time % etc).
// sent/notSent/yetToSend are shown in place of the raw hotelStays count;
// hotelStays itself is kept (unrendered) since recoveryPct still divides
// received by it.
function mapClientBreakdown(clientBreakdown) {
  const rows = (clientBreakdown || []).map((item) => ({
    clientId: item.clientId,
    client: item.clientName,
    tmcSource: item.tmc_data_source || "",
    totalRecords: item.totalRecords || 0,
    total: item.total || 0,
    sentOnTime: item.sentOnTime || 0,
    sentLate: item.sentLate || 0,
    sent: item.sent || 0,
    notSent: item.notSent || 0,
    yetToSend: item.yetToSend || 0,
    onTimePct: Math.round((item.onTimePercent || 0) * 10) / 10,
    hotelStays: item.hotelStays || 0,
    received: item.received || 0,
    recoveryPct: Math.round((item.recoveryPercent || 0) * 10) / 10,
  }));

  const totalRow = rows.reduce(
    (acc, row) => ({
      client: "All clients",
      totalRecords: acc.totalRecords + row.totalRecords,
      total: acc.total + row.total,
      sentOnTime: acc.sentOnTime + row.sentOnTime,
      sentLate: acc.sentLate + row.sentLate,
      sent: acc.sent + row.sent,
      notSent: acc.notSent + row.notSent,
      yetToSend: acc.yetToSend + row.yetToSend,
      hotelStays: acc.hotelStays + row.hotelStays,
      received: acc.received + row.received,
    }),
    { client: "All clients", totalRecords: 0, total: 0, sentOnTime: 0, sentLate: 0, sent: 0, notSent: 0, yetToSend: 0, hotelStays: 0, received: 0 }
  );
  totalRow.onTimePct = totalRow.total ? Math.round((totalRow.sentOnTime / totalRow.total) * 1000) / 10 : 0;
  totalRow.recoveryPct = totalRow.hotelStays ? Math.round((totalRow.received / totalRow.hotelStays) * 1000) / 10 : 0;

  // totalRow is returned separately (not appended to rows) so it can be
  // pinned to the bottom of the grid regardless of column sort.
  return { rows, totalRow: rows.length ? { ...totalRow, isTotal: true } : null };
}

// Mapped from the same gstin-transmission/dashboard/summary response's
// reasonBreakdown array: { key, label, notSent, sentLate } — currently only
// bookingDataLate/systemSentLate/noHotelEmail come back with data. No
// coveredLater or owner field exists in the API yet, so those columns are
// dropped for now (owner used to be hardcoded); total is sentLate + notSent.
function mapLateReasons(reasonBreakdown) {
  const rows = (reasonBreakdown || []).map((item) => ({
    reason: item.label,
    sentLate: item.sentLate || 0,
    notSent: item.notSent || 0,
    total: (item.sentLate || 0) + (item.notSent || 0),
  }));

  const total = rows.reduce(
    (acc, row) => ({
      sentLate: acc.sentLate + row.sentLate,
      notSent: acc.notSent + row.notSent,
      total: acc.total + row.total,
    }),
    { sentLate: 0, notSent: 0, total: 0 }
  );

  return { rows, total };
}

// Plain 3-segment bar, widths as % of row.total — all three categories
// shown proportionally. A recharts version was tried but at this thin a
// height (6px) its SVG bars rendered blank, so plain divs are used instead.
function BreakdownBar({ row }) {
  const segments = BREAKDOWN_SEGMENTS.map((s) => ({ ...s, value: row[s.key] || 0 }));
  // Percentages are based on the segments' own sum (not row.total) so the
  // bar always fills edge-to-edge — row.total can include records that
  // don't fall into any of these 4 buckets, which used to leave a gap.
  const segmentsTotal = segments.reduce((sum, s) => sum + s.value, 0);
  return (
    <div className="rd-breakdown-bar">
      {segments.map((s) => (
        <Tooltip key={s.key} title={`${s.label}: ${fmt(s.value)}`}>
          <div
            style={{ width: segmentsTotal ? `${(s.value / segmentsTotal) * 100}%` : 0, background: s.color }}
          />
        </Tooltip>
      ))}
    </div>
  );
}

function PerformanceRow({ row, bold }) {
  const onTimePct = row.total ? Math.round((row.sentOnTime / row.total) * 100) : 0;
  return (
    <tr className={bold ? "rd-row-total" : undefined}>
      <td>{row.label}</td>
      <td className="rd-num">{fmt(row.total)}</td>
      <td className="rd-num rd-c-green">{fmt(row.sentOnTime)}</td>
      <td className="rd-num rd-c-amber">{fmt(row.sentLate)}</td>
      <td className="rd-num rd-c-red">{fmt(row.notSent)}</td>
      <td className="rd-num rd-c-muted">{fmt(row.yetToSend)}</td>
      <td className="rd-breakdown-cell">
        <BreakdownBar row={row} />
      </td>
      <td className="rd-num rd-c-strong">{onTimePct}%</td>
    </tr>
  );
}

const CLIENT_BREAKDOWN_COLUMN_DEFS = [
  {
    field: "client",
    headerName: "Client",
    pinned: "left",
    flex: 2,
    minWidth: 200,
    filter: "agTextColumnFilter",
    // agGroupCellRenderer is what actually draws the expand/collapse chevron
    // for master/detail rows — masterDetail=true alone does not add it,
    // that only happens automatically when real row grouping/tree data is
    // active. innerRenderer keeps our own label inside that same cell.
    cellRenderer: "agGroupCellRenderer",
    cellRendererParams: {
      suppressCount: true,
      innerRenderer: (params) => <span className="rd-client-name">{params.value}</span>,
    },
  },
  { field: "totalRecords", headerName: "Total Records", flex: 1, minWidth: 120, cellClass: "ag-right-aligned-cell", filter: "agNumberColumnFilter", valueFormatter: (p) => fmt(p.value) },
  { field: "total", headerName: "Eligible to send the mail", flex: 1.4, minWidth: 180, cellClass: "ag-right-aligned-cell", filter: "agNumberColumnFilter", valueFormatter: (p) => fmt(p.value) },
  {
    field: "breakdown",
    headerName: "Breakdown",
    flex: 1.5,
    minWidth: 160,
    sortable: false,
    filter: false,
    cellRenderer: (params) => <BreakdownBar row={params.data} />,
  },
  { field: "onTimePct", headerName: "On-time %", flex: 1, minWidth: 100, cellClass: "ag-right-aligned-cell rd-c-strong", filter: "agNumberColumnFilter", valueFormatter: (p) => `${p.value}%` },
  { field: "sent", headerName: "Sent", flex: 1, minWidth: 100, cellClass: "ag-right-aligned-cell rd-c-green", filter: "agNumberColumnFilter", valueFormatter: (p) => fmt(p.value) },
  { field: "sentOnTime", headerName: "Sent On Time", flex: 1, minWidth: 110, cellClass: "ag-right-aligned-cell rd-c-green", filter: "agNumberColumnFilter", valueFormatter: (p) => fmt(p.value) },
  { field: "sentLate", headerName: "Sent Late", flex: 1, minWidth: 100, cellClass: "ag-right-aligned-cell rd-c-amber", filter: "agNumberColumnFilter", valueFormatter: (p) => fmt(p.value) },
  { field: "notSent", headerName: "Not sent", flex: 1, minWidth: 100, cellClass: "ag-right-aligned-cell rd-c-red", filter: "agNumberColumnFilter", valueFormatter: (p) => fmt(p.value) },
  { field: "yetToSend", headerName: "Yet to send", flex: 1, minWidth: 110, cellClass: "ag-right-aligned-cell rd-c-muted", filter: "agNumberColumnFilter", valueFormatter: (p) => fmt(p.value) },
  { field: "received", headerName: "Received", flex: 1, minWidth: 100, cellClass: "ag-right-aligned-cell rd-c-green", filter: "agNumberColumnFilter", valueFormatter: (p) => fmt(p.value) },
  {
    field: "recoveryPct",
    headerName: "Recovery",
    flex: 1,
    minWidth: 100,
    filter: "agNumberColumnFilter",
    cellClass: (p) => `ag-right-aligned-cell ${p.value >= 70 ? "rd-c-green" : "rd-c-red"}`,
    valueFormatter: (p) => `${p.value}%`,
  },
  {
    field: "tmcSource",
    headerName: "TMC Source",
    pinned: "right",
    flex: 1,
    minWidth: 120,
    filter: "agTextColumnFilter",
    sort: "asc",
    cellRenderer: (params) =>
      params.value ? <span className="rd-tmc-tag">{params.value}</span> : null,
  },
];

// Counts distinct GSTINs within a group rather than total rows, since the
// same GSTIN can legitimately appear more than once per customer name.
const distinctGstinCount = (params) => {
  const leafChildren = params.rowNode?.allLeafChildren || [];
  const distinctGstins = new Set(
    leafChildren.map((node) => node.data?.gstin).filter(Boolean)
  );
  return distinctGstins.size;
};

// Counts how many rows in a group have authStatus === "Active".
const activeAuthCount = (params) => {
  const leafChildren = params.rowNode?.allLeafChildren || [];
  return leafChildren.filter((node) => node.data?.authStatus === "Active").length;
};

// Same field names as HotelEmail.jsx's CUSTOMER_INFO_COLUMN_DEFS, since both
// read rows straight from the customerDetails endpoint.
const GSTIN_DETAIL_COLUMN_DEFS = [
  {
    field: "customer_code",
    headerName: "Customer Code",
    flex: 1,
    minWidth: 130,
    filter: "agTextColumnFilter",
    aggFunc: distinctGstinCount,
    enableRowGroup:true
  },
  { field: "client", headerName: "Client", flex: 1.4, minWidth: 180, filter: "agTextColumnFilter", enableRowGroup: true, rowGroup: true, hide: true },
  { field: "name", headerName: "Name", flex: 1.6, minWidth: 200, filter: "agTextColumnFilter", enableRowGroup: true },
  { field: "gstin", headerName: "GSTIN", flex: 1.2, minWidth: 160, filter: "agTextColumnFilter", aggFunc: distinctGstinCount, enableRowGroup:true },
  { field: "pan", headerName: "PAN", flex: 1, minWidth: 120, filter: "agTextColumnFilter", enableRowGroup: true },
  { field: "stateCd", headerName: "State Code", flex: 0.8, minWidth: 100, filter: "agTextColumnFilter", enableRowGroup: true },
  {
    field: "authStatus",
    headerName: "Auth Status",
    flex: 1,
    minWidth: 130,
    filter: "agTextColumnFilter",
    enableRowGroup: true,
    aggFunc: activeAuthCount,
    cellRenderer: (params) => {
      if (params.node.group) return params.value;
      if (!params.value) return null;
      const isAuthenticated = params.value === "Active";
      return (
        <Tag color={isAuthenticated ? "green" : "red"}>{params.value}</Tag>
      );
    },
  },
  { field: "taxpayertype", headerName: "Taxpayer Type", flex: 1, minWidth: 140, filter: "agTextColumnFilter", enableRowGroup: true },
  { field: "gstin_address", headerName: "GSTIN Address", flex: 2.4, minWidth: 260, filter: "agTextColumnFilter", enableRowGroup: true },
];

const QUICK_DATE_PRESETS = [
  { label: "Today", key: "today" },
  { label: "Yesterday", key: "yesterday" },
  { label: "Last 7 days", key: "last7" },
  { label: "Last 30 days", key: "last30" },
  { label: "This month", key: "thisMonth" },
  { label: "Last month", key: "lastMonth" },
];

// Same clientnames endpoint/payload as HotelEmail.jsx's "Select Hotel Client"
// filter, so this dashboard's client list always matches the main page.
const API_CLIENT_NAMES = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/clientnames";

function ReminderDashboard() {
  const domain = getSubdomain();
  const [userDetails] = useRecoilState(userInfo);

  const segmentType = "HOTEL";
  const hotelFollowUpType = "GSTIN_TRANSMISSION";
  const [clientNames, setClientNames] = useState([]);
  const [clientNamesLoading, setClientNamesLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(() => loadStoredFilters()?.selectedClient ?? []);
  const [dateFilter, setDateFilter] = useState(() => loadStoredFilters()?.dateFilter ?? "");
  const [activeDatePreset, setActiveDatePreset] = useState(() => loadStoredFilters()?.activeDatePreset ?? "");
  const [fromDate, setFromDate] = useState(() => {
    const saved = loadStoredFilters()?.fromDate;
    return saved ? moment(saved) : null;
  });
  const [toDate, setToDate] = useState(() => {
    const saved = loadStoredFilters()?.toDate;
    return saved ? moment(saved) : null;
  });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  // "ALL" | "MATCH" | "MISMATCH" — sent to the summary API as gstinMatchStatus.
  const [gstinMatchStatus, setGstinMatchStatus] = useState(() => loadStoredFilters()?.gstinMatchStatus ?? "ALL");
  const [selectedStates, setSelectedStates] = useState(() => loadStoredFilters()?.selectedStates ?? []);
  const [selectedCities, setSelectedCities] = useState(() => loadStoredFilters()?.selectedCities ?? []);
  const [selectedHotels, setSelectedHotels] = useState(() => loadStoredFilters()?.selectedHotels ?? []);

  // Keep localStorage in sync whenever a filter changes.
  useEffect(() => {
    try {
      localStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify({
          selectedClient,
          dateFilter,
          activeDatePreset,
          fromDate: fromDate ? fromDate.format("YYYY-MM-DD") : null,
          toDate: toDate ? toDate.format("YYYY-MM-DD") : null,
          gstinMatchStatus,
          selectedStates,
          selectedCities,
          selectedHotels,
        })
      );
    } catch (error) {
      // localStorage unavailable (private mode, quota, etc.) — filters just
      // won't persist across navigation.
    }
  }, [
    selectedClient,
    dateFilter,
    activeDatePreset,
    fromDate,
    toDate,
    gstinMatchStatus,
    selectedStates,
    selectedCities,
    selectedHotels,
  ]);

  // BCD clients need the customerDetails API for client breakdown (same
  // domain/client_id-prefix check HotelEmail.jsx uses to gate its BCD-only
  // "View Customer Info" button).
  const isBcd =
    domain?.toLowerCase() === "bcd" ||
    (Array.isArray(selectedClient) &&
      selectedClient.some((id) => id?.startsWith("BCD_") || id?.startsWith("HALEON_")));
  const [customerDetails, setCustomerDetails] = useState([]);
  const [customerDetailsLoading, setCustomerDetailsLoading] = useState(false);

  useEffect(() => {
    if (!isBcd || !selectedClient?.length) {
      setCustomerDetails([]);
      return;
    }
    let cancelled = false;
    // Fetched one client_id at a time (rather than one combined request) so
    // each row can be tagged with which selected client it came from — the
    // API's response has no client identifier of its own, which is what the
    // "group by client" view below needs.
    const fetchOneClient = async (clientId) => {
      const clientLabel = clientNames.find((c) => c.client_id === clientId)?.client_name || clientId;
      try {
        const res = await fetch(API_CUSTOMER_DETAILS, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ client_id: [clientId] }),
        });
        const response = await res.json().catch(() => null);
        const normalizedHaleonClient = (response?.haleon_client || []).map((row) => ({
          ...row,
          customer_code: row.customer_code ?? row.dkno,
          name: row.name ?? row.entity,
          gstin_address: row.gstin_address ?? row.address,
        }));
        const rows = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : [
              ...(response?.bcd_client || []),
              ...normalizedHaleonClient,
            ];
        return rows.map((row) => ({ ...row, client: clientLabel }));
      } catch (error) {
        return [];
      }
    };
    const fetchCustomerDetails = async () => {
      setCustomerDetailsLoading(true);
      const results = await Promise.all(selectedClient.map(fetchOneClient));
      if (!cancelled) setCustomerDetails(results.flat());
      if (!cancelled) setCustomerDetailsLoading(false);
    };
    fetchCustomerDetails();
    return () => {
      cancelled = true;
    };
  }, [isBcd, selectedClient, clientNames]);

  // Hotel/State/City filter dropdown options.
  const [hotelCityStateOptions, setHotelCityStateOptions] = useState([]);

  useEffect(() => {
    if (!selectedClient?.length) {
      setHotelCityStateOptions([]);
      return;
    }
    let cancelled = false;
    const fetchHotelCityState = async () => {
      try {
        const res = await fetch(API_HOTEL_CITY_STATE, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ clientId: selectedClient }),
        });
        const response = await res.json().catch(() => null);
        if (cancelled) return;
        setHotelCityStateOptions(response?.status ? response.data || [] : []);
      } catch (error) {
        if (!cancelled) setHotelCityStateOptions([]);
      }
    };
    fetchHotelCityState();
    return () => {
      cancelled = true;
    };
  }, [selectedClient]);

  // hotelName/hotelCity/hotelState can each come back as null, a non-string,
  // or an empty array for some hotels, so every field needs the same guard.
  const asTrimmedString = (value) => (typeof value === "string" ? value.trim() : "");
  const hotelNameOptions = useMemo(
    () => Array.from(new Set(hotelCityStateOptions.map((h) => asTrimmedString(h.hotelName)).filter(Boolean))).sort(),
    [hotelCityStateOptions]
  );
  const hotelStateOptions = useMemo(
    () => Array.from(new Set(hotelCityStateOptions.map((h) => asTrimmedString(h.hotelState)).filter(Boolean))).sort(),
    [hotelCityStateOptions]
  );
  const hotelCityOptions = useMemo(
    () => Array.from(new Set(hotelCityStateOptions.map((h) => asTrimmedString(h.hotelCity)).filter(Boolean))).sort(),
    [hotelCityStateOptions]
  );


  // Guards against the summary API's responses resolving out of order: while
  // multi-selecting clients, each change fires a new request, and a slower
  // older one can resolve after a newer one and overwrite it with stale
  // numbers — which looked like a glitch. Only the latest request's response
  // is ever applied, and the call itself is debounced so a burst of quick
  // selections doesn't fire one request per click.
  const summaryRequestIdRef = useRef(0);

  useEffect(() => {
    const requestId = ++summaryRequestIdRef.current;
    setSummaryLoading(true);

    const timeoutId = setTimeout(async () => {
      try {
        const res = await fetch(API_GSTIN_DASHBOARD_SUMMARY, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            domain,
            gstin: localStorage.getItem("hotelGstin"),
            type: segmentType,
            serviceType: hotelFollowUpType,
            clientIds: selectedClient,
            dateFilter,
            gstinMatchStatus,
            states: selectedStates,
            cities: selectedCities,
            hotels: selectedHotels,
          }),
        });
        const response = await res.json().catch(() => null);
        if (requestId !== summaryRequestIdRef.current) return; // a newer request has since fired
        if (response?.status) setSummary(response.data);
      } catch (error) {
        // Best-effort; cards fall back to zeroes.
      } finally {
        if (requestId === summaryRequestIdRef.current) setSummaryLoading(false);
      }
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [segmentType, hotelFollowUpType, selectedClient, dateFilter, gstinMatchStatus, selectedStates, selectedCities, selectedHotels]);

  useEffect(() => {
    const fetchClientNames = async () => {
      setClientNamesLoading(true);
      try {
        const res = await fetch(API_CLIENT_NAMES, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: segmentType.charAt(0).toUpperCase() + segmentType.slice(1).toLowerCase(),
            domain,
            serviceType: hotelFollowUpType || undefined,
            userDetails: {
              email: userDetails?.email,
              currentWorkspace: userDetails?.currentWorkspace?.name,
              workspaceId: userDetails?.currentWorkspace?.id,
            },
          }),
        });
        const response = await res.json();
        if (response.status) {
          const data = response.data || [];
          setClientNames(data);
          setSelectedClient(data.map((c) => c.client_id));
        } else {
          setClientNames([]);
        }
      } catch (error) {
        setClientNames([]);
      } finally {
        setClientNamesLoading(false);
      }
    };
    fetchClientNames();
  }, [segmentType, hotelFollowUpType]);

  // Mirrors HotelEmail.jsx's "Select Hotel Client" handleChange — supports
  // the synthetic "__all__" toggle option below.
  const handleClientChange = (value) => {
    if (Array.isArray(value) && value.includes("__all__")) {
      const allIds = clientNames.map((c) => c.client_id);
      const alreadyAllSelected = Array.isArray(selectedClient) && allIds.every((id) => selectedClient.includes(id));
      setSelectedClient(alreadyAllSelected ? [] : allIds);
    } else {
      setSelectedClient(value);
    }
  };

  const dateFilterLabel = useMemo(() => {
    if (!dateFilter) return "Select date range";
    if (dateFilter.includes(",")) {
      const [s, e] = dateFilter.split(",");
      return `${moment(s).format("DD MMM YYYY")}  →  ${moment(e).format("DD MMM YYYY")}`;
    }
    return moment(dateFilter).format("DD MMM YYYY");
  }, [dateFilter]);

  // Mapped from the gstin-transmission/dashboard/summary response:
  // { totalRecords, sent, notSent, lateArrival, emailRequired,
  //   gstinNotRegistered, bounce, deferred, dropped }
  const totalRecords = summary?.totalRecords || 0;
  const sent = summary?.sent || 0;
  const notSent = summary?.notSent || 0;
  const whyNotSentItems = [
    { label: "Arrived after the daily run", value: summary?.lateArrival || 0 },
    { label: "No hotel email on file", value: summary?.emailRequired || 0 },
    { label: "GSTIN not registered", value: summary?.gstinNotRegistered || 0 },
    { label: "Low email score", value: summary?.low_email_score || 0 },
    { label: "Our system did not send it", value: summary?.systemNotSent || 0 },
  ];
  const emailDelivery = {
    bounced: summary?.bounce || 0,
    deferred: summary?.deferred || 0,
    dropped: summary?.dropped || 0,
  };

  const performanceStats = {
    hotelStays: summary?.hotelEligibleCount || 0,
    invoicesReceived: summary?.hotelEligibleReceived || 0,
    stillAwaiting: summary?.hotelEligibleNotReceived || 0,
    invoiceRecoveryPct: summary?.hotelEligibleCount
      ? Math.round((summary.hotelEligibleReceived / summary.hotelEligibleCount) * 1000) / 10
      : 0,
  };

  const reminderBreakdown = summary?.reminderBreakdown || [];
  const { rows: clientBreakdownGridRows, totalRow: clientBreakdownTotalRow } = mapClientBreakdown(summary?.clientBreakdown);
  // const { rows: lateReasonRows, total: lateReasonTotal } = mapLateReasons(summary?.reasonBreakdown);
  const performanceGroups = [
    { group: "Reminders to the hotel", rows: reminderBreakdown.filter((r) => r.category === "hotel") },
    { group: "Reminders to the traveller", rows: reminderBreakdown.filter((r) => r.category === "traveller") },
  ].map((g) => ({
    group: g.group,
    rows: g.rows.map((item) => ({
      label: cleanBreakdownLabel(item.label, item.category),
      total: item.total || 0,
      sentOnTime: item.sentOnTime || 0,
      sentLate: item.sentLate || 0,
      notSent: item.notSent || 0,
      yetToSend: item.yetToSend || 0,
    })),
  }));

  // Same donut series shape as HotelEmail.jsx's mailDispatchChartOptions.
  const remindersSentChartOptions = {
    data: [
      { category: "Sent", value: sent },
      { category: "Not sent", value: notSent },
    ],
    series: [{
      type: "donut",
      angleKey: "value",
      calloutLabelKey: "category",
      innerRadiusRatio: 0.68,
      fills: ["#26A69A", "#F87171"],
      strokes: ["#FFFFFF"],
      strokeWidth: 2,
      calloutLabel: { enabled: false },
      sectorLabel: { enabled: false },
      tooltip: {
        renderer: ({ datum }) => ({ title: datum.category, content: fmt(datum.value) }),
      },
    }],
    background: { fill: "transparent" },
    legend: { enabled: false },
    padding: { top: 0, right: 0, bottom: 0, left: 0 },
  };

  return (
    <div className="rd-page">
      <PageHeader title="Reminder Dashboard" showWorkspaceDropdown={false} goBack />

      <div style={{ padding: "0 20px 20px" }}>
        <div className="rd-filters">
          <div className="rd-filter">
            <div className="rd-filter-label">Select Hotel Client</div>
            <Select
              mode="multiple"
              className="compact-client-select rd-filter-input"
              loading={clientNamesLoading}
              value={selectedClient || undefined}
              onChange={handleClientChange}
              placeholder="Select Hotel Client"
              allowClear
              showSearch
              tagRender={(props) => {
                const { value, closable, onClose } = props;
                const selectedArr = Array.isArray(selectedClient) ? selectedClient : [];
                const idx = selectedArr.indexOf(value);
                const clientName = clientNames.find((c) => c.client_id === value)?.client_name || value;

                if (idx === 0) {
                  return (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      background: "rgba(10,131,148,0.08)",
                      border: "1px solid rgba(10,131,148,0.35)",
                      borderRadius: 4,
                      padding: "0 6px 0 8px",
                      fontSize: 12,
                      color: "#0A8394",
                      height: 22,
                      maxWidth: 140,
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{clientName}</span>
                      {closable && (
                        <span
                          onClick={(e) => { e.stopPropagation(); onClose(); }}
                          style={{ cursor: "pointer", color: "#0A8394", fontSize: 14, lineHeight: 1, flexShrink: 0, opacity: 0.7 }}
                        >×</span>
                      )}
                    </span>
                  );
                }

                if (idx === 1) {
                  const remaining = selectedArr.slice(1);
                  const remainingItems = remaining.map((id) => ({
                    id,
                    name: clientNames.find((c) => c.client_id === id)?.client_name || id,
                  }));
                  return (
                    <Tooltip
                      color="#1C1C1C"
                      overlayInnerStyle={{ padding: 10, minWidth: 180, borderRadius: 8 }}
                      title={
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {remainingItems.map((item) => (
                            <div key={item.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                              <span style={{ fontSize: 12, color: "#fff" }}>{item.name}</span>
                              <span
                                style={{ cursor: "pointer", color: "#aaa", fontSize: 16, lineHeight: 1, padding: "0 2px", flexShrink: 0 }}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleClientChange(selectedArr.filter((id) => id !== item.id));
                                }}
                              >×</span>
                            </div>
                          ))}
                        </div>
                      }
                    >
                      <span style={{
                        background: "#e6f4ff", color: "#1677ff", borderRadius: 999,
                        padding: "0 8px", fontSize: 12, fontWeight: 500,
                        cursor: "pointer", whiteSpace: "nowrap",
                        display: "inline-flex", alignItems: "center",
                        height: 22, marginLeft: 4, flexShrink: 0,
                      }}>
                        +{remaining.length} more
                      </span>
                    </Tooltip>
                  );
                }

                // idx > 1: already counted in "+N more", render nothing
                return <span key={value} style={{ display: "none" }} />;
              }}
              filterOption={(input, option) =>
                (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())
              }
            >
              {(() => {
                const allSelected =
                  Array.isArray(selectedClient) &&
                  clientNames.length > 0 &&
                  clientNames.every((item) => selectedClient.includes(item.client_id));
                return (
                  <>
                    <Select.Option key="__all__" value="__all__">
                      <span style={{ fontWeight: 600, color: allSelected ? "#0A8394" : undefined }}>
                        {allSelected ? "✓ All Selected" : "Select All"}
                      </span>
                    </Select.Option>
                    {clientNames.map((item, key) => (
                      <Select.Option key={key} value={item?.client_id}>
                        {item?.client_name}
                      </Select.Option>
                    ))}
                  </>
                );
              })()}
            </Select>
          </div>

          <div className="rd-filter">
            <div className="rd-filter-label">Date Filter</div>
            <Popover
              open={datePickerOpen}
              onOpenChange={setDatePickerOpen}
              trigger="click"
              placement="bottomLeft"
              overlayInnerStyle={{ padding: 0, borderRadius: 6, overflow: "hidden" }}
              content={
                <div style={{ display: "flex", width: 480, fontSize: 13 }}>
                  {/* Left: absolute range */}
                  <div style={{ flex: "0 0 200px", padding: 16, borderRight: "1px solid #f0f0f0" }}>
                    <div style={{ fontWeight: 600, marginBottom: 12, color: "#333" }}>Absolute time range</div>

                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>From</div>
                      <DatePicker
                        size="small"
                        style={{ width: "100%" }}
                        value={fromDate}
                        format="DD/MM/YYYY"
                        onChange={(d) => setFromDate(d)}
                        allowClear
                      />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                        To <span style={{ color: "#bbb" }}>(optional)</span>
                      </div>
                      <DatePicker
                        size="small"
                        style={{ width: "100%" }}
                        value={toDate}
                        format="DD/MM/YYYY"
                        onChange={(d) => setToDate(d)}
                        allowClear
                        disabledDate={(curr) => fromDate && curr && curr.isBefore(fromDate, "day")}
                      />
                    </div>

                    <button
                      className="datePresetBtn active"
                      style={{ width: "100%", textAlign: "center", padding: "5px 0" }}
                      disabled={!fromDate}
                      onClick={() => {
                        if (!fromDate) return;
                        const value = toDate
                          ? `${fromDate.format("YYYY-MM-DD")},${toDate.format("YYYY-MM-DD")}`
                          : fromDate.format("YYYY-MM-DD");
                        setDateFilter(value);
                        setActiveDatePreset("custom");
                        setDatePickerOpen(false);
                      }}
                    >
                      Apply time range
                    </button>
                  </div>

                  {/* Right: quick presets */}
                  <div style={{ flex: 1 }}>
                    <div style={{ padding: "12px 14px 6px", fontSize: 12, color: "#888", borderBottom: "1px solid #f5f5f5" }}>
                      Quick ranges
                    </div>

                    {QUICK_DATE_PRESETS.map(({ label, key }) => (
                      <div
                        key={key}
                        className={`dateQuickPreset${activeDatePreset === key ? " active" : ""}`}
                        onClick={() => {
                          const t = moment();
                          let value = "";

                          if (key === "today") {
                            value = t.format("YYYY-MM-DD");
                          } else if (key === "yesterday") {
                            value = t.clone().subtract(1, "days").format("YYYY-MM-DD");
                          } else if (key === "last7") {
                            value = `${t.clone().subtract(6, "days").format("YYYY-MM-DD")},${t.format("YYYY-MM-DD")}`;
                          } else if (key === "last30") {
                            value = `${t.clone().subtract(29, "days").format("YYYY-MM-DD")},${t.format("YYYY-MM-DD")}`;
                          } else if (key === "thisMonth") {
                            value = `${t.clone().startOf("month").format("YYYY-MM-DD")},${t.format("YYYY-MM-DD")}`;
                          } else if (key === "lastMonth") {
                            const lm = t.clone().subtract(1, "month");
                            value = `${lm.startOf("month").format("YYYY-MM-DD")},${lm.clone().endOf("month").format("YYYY-MM-DD")}`;
                          }

                          setActiveDatePreset(key);
                          setDateFilter(value);
                          setFromDate(null);
                          setToDate(null);
                          setDatePickerOpen(false);
                        }}
                      >
                        {label}
                      </div>
                    ))}
                  </div>
                </div>
              }
            >
              <button className={`dateFilterTrigger${dateFilter ? " hasValue" : ""}`}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                  <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <span style={{ flex: 1 }}>{dateFilterLabel}</span>
                {dateFilter && (
                  <span
                    className="dateFilterClear"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDateFilter("");
                      setActiveDatePreset("");
                      setFromDate(null);
                      setToDate(null);
                    }}
                  >
                    ×
                  </span>
                )}
              </button>
            </Popover>
          </div>

          <div className="rd-filter">
            <div className="rd-filter-label">Hotel</div>
            <Select
              mode="multiple"
              className="rd-filter-input"
              style={{ minWidth: 180 }}
              value={selectedHotels}
              onChange={setSelectedHotels}
              placeholder="All hotels"
              allowClear
              showSearch
              filterOption={(input, option) => (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())}
            >
              {hotelNameOptions.map((hotel) => (
                <Select.Option key={hotel} value={hotel}>
                  {hotel}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="rd-filter">
            <div className="rd-filter-label">GstMatch</div>
            <Select
              className="rd-filter-input"
              style={{ minWidth: 200 }}
              value={gstinMatchStatus}
              onChange={setGstinMatchStatus}
              placeholder="All status"
            >
              <Select.Option value="ALL">All status</Select.Option>
              <Select.Option value="MATCH">Match</Select.Option>
              <Select.Option value="MISMATCH">Mismatch</Select.Option>
            </Select>
          </div>

          <div className="rd-filter">
            <div className="rd-filter-label">State</div>
            <Select
              mode="multiple"
              className="rd-filter-input"
              style={{ minWidth: 180 }}
              value={selectedStates}
              onChange={setSelectedStates}
              placeholder="All states"
              allowClear
              showSearch
              filterOption={(input, option) => (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())}
            >
              {hotelStateOptions.map((state) => (
                <Select.Option key={state} value={state}>
                  {state}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="rd-filter">
            <div className="rd-filter-label">City</div>
            <Select
              mode="multiple"
              className="rd-filter-input"
              style={{ minWidth: 180 }}
              value={selectedCities}
              onChange={setSelectedCities}
              placeholder="All cities"
              allowClear
              showSearch
              filterOption={(input, option) => (option?.children ?? "").toString().toLowerCase().includes(input.toLowerCase())}
            >
              {hotelCityOptions.map((city) => (
                <Select.Option key={city} value={city}>
                  {city}
                </Select.Option>
              ))}
            </Select>
          </div>
        </div>

        <div className="rd-summary-container">
          {summaryLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rd-summary-card">
                <Skeleton active paragraph={{ rows: 4 }} />
              </div>
            ))
          ) : (
            <>
              <div className="rd-summary-card">
                <div className="rd-summary-title-row">
                  <div className="rd-summary-title">REMINDERS SENT</div>
                </div>
                <div className="rd-summary-content">
                  <div className="rd-summary-chart">
                    <AgCharts options={remindersSentChartOptions} style={{ height: "138px" }} />
                  </div>
                  <div className="rd-summary-stats">
                    <div className="rd-summary-muted">Total reminders</div>
                    <div className="rd-summary-big-number">{fmt(totalRecords)}</div>
                    <div className="rd-summary-split-row">
                      <div className="rd-summary-split-item">
                        <div className="rd-summary-legend-row">
                          <span className="rd-dot" style={{ background: "#26A69A" }} />
                          <span className="rd-summary-muted">Sent</span>
                        </div>
                        <div className="rd-summary-value" style={{ color: "#26A69A" }}>{fmt(sent)}</div>
                      </div>
                      <div className="rd-summary-split-item">
                        <div className="rd-summary-legend-row">
                          <span className="rd-dot" style={{ background: "#F87171" }} />
                          <span className="rd-summary-muted">Not sent</span>
                        </div>
                        <div className="rd-summary-value" style={{ color: "#F87171" }}>{fmt(notSent)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rd-card">
                <div className="rd-card-header">
                  <span className="rd-card-title">WHY NOT SENT</span>
                  <span className="rd-badge" style={{ color: "#dc2626", background: "#fee2e2" }}>
                    {fmt(notSent)} to action
                  </span>
                </div>
                <div className="rd-why-list">
                  {whyNotSentItems.map((item) => (
                    <div className="rd-why-row" key={item.label}>
                      <span>{item.label}</span>
                      <span className="rd-num">{fmt(item.value)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rd-card">
                <div className="rd-card-header">
                  <span className="rd-card-title">EMAIL DELIVERY</span>
                  <span className="rd-badge" style={{ color: "#dc2626", background: "#fee2e2" }}>
                    {fmt(emailDelivery.bounced + emailDelivery.deferred + emailDelivery.dropped)} issues
                  </span>
                </div>
                <div className="rd-why-list">
                  <div className="rd-why-row">
                    <span>Bounced</span>
                    <span className="rd-num rd-c-strong">{fmt(emailDelivery.bounced)}</span>
                  </div>
                  <div className="rd-why-row">
                    <span>Deferred</span>
                    <span className="rd-num rd-c-strong">{fmt(emailDelivery.deferred)}</span>
                  </div>
                  <div className="rd-why-row">
                    <span>Dropped</span>
                    <span className="rd-num rd-c-strong">{fmt(emailDelivery.dropped)}</span>
                  </div>
                </div>
                <div className="rd-footnote">
                  Counted after sending. A reminder whose addresses all bounced is shown as not sent.
                </div>
              </div>
            </>
          )}
        </div>

        <div className="rd-performance-card">
          <div className="rd-card-header">
            <span className="rd-card-title">REMINDER PERFORMANCE</span>
            <span className="rd-footnote-inline">one row per reminder · one count per booking</span>
          </div>

          {summaryLoading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : (
            <>
              <div className="rd-stats-row">
                <div className="rd-stat-block">
                  <div className="rd-stat-block-label">HOTEL STAYS</div>
                  <div className="rd-stat-block-value">{fmt(performanceStats.hotelStays)}</div>
                  <div className="rd-stat-block-sub">bookings in this period</div>
                </div>
                <div className="rd-stat-block">
                  <div className="rd-stat-block-label">INVOICES RECEIVED</div>
                  <div className="rd-stat-block-value rd-c-green">{fmt(performanceStats.invoicesReceived)}</div>
                  <div className="rd-stat-block-sub">usable invoices back</div>
                </div>
                <div className="rd-stat-block">
                  <div className="rd-stat-block-label">INVOICE RECOVERY</div>
                  <div className="rd-stat-block-value">{performanceStats.invoiceRecoveryPct}%</div>
                  <div className="rd-stat-block-sub">
                    {fmt(performanceStats.invoicesReceived)} of {fmt(performanceStats.hotelStays)} stays
                  </div>
                </div>
                <div className="rd-stat-block">
                  <div className="rd-stat-block-label">STILL AWAITING</div>
                  <div className="rd-stat-block-value rd-c-red">{fmt(performanceStats.stillAwaiting)}</div>
                  <div className="rd-stat-block-sub">no invoice yet</div>
                </div>
              </div>

              <table className="rd-table">
                <thead>
                  <tr>
                    <th>When it goes out</th>
                    <th className="rd-num">Total</th>
                    <th className="rd-num">Sent on time</th>
                    <th className="rd-num">Sent late</th>
                    <th className="rd-num">Not sent</th>
                    <th className="rd-num">Yet to send</th>
                    <th className="rd-breakdown-cell">Breakdown</th>
                    <th className="rd-num">On-time %</th>
                  </tr>
                </thead>
                <tbody>
                  {performanceGroups.map((g) => (
                    <Fragment key={g.group}>
                      <tr className="rd-group-row">
                        <td colSpan={8}>{g.group.toUpperCase()}</td>
                      </tr>
                      {g.rows.map((row) => (
                        <PerformanceRow key={row.label} row={row} />
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>

              <div className="rd-legend-row">
                {BREAKDOWN_SEGMENTS.map((s) => (
                  <span className="rd-legend-chip" key={s.key}>
                    <span className="rd-dot" style={{ background: s.color }} /> {s.label}
                  </span>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="rd-performance-card">
          <div className="rd-card-header">
            <span className="rd-card-title">CLIENT BREAKDOWN</span>
            <span className="rd-footnote-inline">same reminders, grouped by client</span>
          </div>

          <div className="ag-theme-alpine">
            <AgTableClient
              domLayout="autoHeight"
              hideToolbar
              rowData={clientBreakdownGridRows}
              pinnedBottomRowData={clientBreakdownTotalRow ? [clientBreakdownTotalRow] : []}
              columnDefs={CLIENT_BREAKDOWN_COLUMN_DEFS}
              defaultColDef={{ sortable: true, resizable: true }}
            />
          </div>
        </div>

        <div className="rd-performance-card">
          <div className="rd-card-header">
            <span className="rd-card-title">CUSTOMER DETAILS</span>
            <span className="rd-footnote-inline">GSTINs serviced across all clients</span>
          </div>

          <div className="ag-theme-alpine" style={{ height: 470 }}>
            {customerDetailsLoading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
              <AgTableClient
                groupPanel
                suppressAggFuncInHeader
                rowData={customerDetails}
                columnDefs={GSTIN_DETAIL_COLUMN_DEFS}
                defaultColDef={{ sortable: true, resizable: true }}
              />
            )}
          </div>
        </div>

        {/*
        <div className="rd-performance-card">
          <div className="rd-card-header">
            <span className="rd-card-title">LATE AND MISSED REMINDERS</span>
            <span className="rd-footnote-inline">{fmt(lateReasonTotal.total)} reminders not sent on time</span>
          </div>

          {summaryLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <table className="rd-table">
              <thead>
                <tr>
                  <th>Why it was late</th>
                  <th className="rd-num">Sent late</th>
                  <th className="rd-num">Not sent</th>
                  <th className="rd-num">Total</th>
                </tr>
              </thead>
              <tbody>
                {lateReasonRows.map((row) => (
                  <tr key={row.reason}>
                    <td>{row.reason}</td>
                    <td className="rd-num rd-c-amber">{fmt(row.sentLate)}</td>
                    <td className="rd-num rd-c-red">{fmt(row.notSent)}</td>
                    <td className="rd-num">{fmt(row.total)}</td>
                  </tr>
                ))}
                <tr className="rd-row-total">
                  <td>Total</td>
                  <td className="rd-num">{fmt(lateReasonTotal.sentLate)}</td>
                  <td className="rd-num">{fmt(lateReasonTotal.notSent)}</td>
                  <td className="rd-num">{fmt(lateReasonTotal.total)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
        */}
      </div>

    </div>
  );
}

export default ReminderDashboard;
